import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Volume2, VolumeX } from 'lucide-react';

/**
 * YouTube Video Background Component with working Mute/Unmute
 * Uses YouTube IFrame API for proper audio control
 */
export const VideoBackground = ({ 
  videoId = "frLPZkVTQUg",
  startTime = 14,
  className = "",
  overlay = true,
  overlayOpacity = 0.6,
  parallaxSpeed = 0.5,
  showMuteButton = true,
  muted = true
}) => {
  const [isMuted, setIsMuted] = useState(muted);
  const [scrollY, setScrollY] = useState(0);
  const [playerReady, setPlayerReady] = useState(false);
  const playerRef = useRef(null);
  const containerRef = useRef(null);
  const playerIdRef = useRef(`yt-player-${Math.random().toString(36).substr(2, 9)}`);

  // Load YouTube IFrame API
  useEffect(() => {
    // Check if API is already loaded
    if (window.YT && window.YT.Player) {
      initPlayer();
      return;
    }

    // Load the API
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    const firstScriptTag = document.getElementsByTagName('script')[0];
    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

    // Set up callback
    window.onYouTubeIframeAPIReady = () => {
      initPlayer();
    };

    return () => {
      if (playerRef.current) {
        playerRef.current.destroy();
      }
    };
  }, []);

  const initPlayer = useCallback(() => {
    if (playerRef.current) return;

    playerRef.current = new window.YT.Player(playerIdRef.current, {
      videoId: videoId,
      playerVars: {
        autoplay: 1,
        mute: 1, // Start muted (required for autoplay)
        loop: 1,
        playlist: videoId,
        start: startTime,
        controls: 0,
        showinfo: 0,
        rel: 0,
        modestbranding: 1,
        playsinline: 1,
        iv_load_policy: 3,
        disablekb: 1,
        fs: 0,
        cc_load_policy: 0,
        origin: window.location.origin
      },
      events: {
        onReady: (event) => {
          setPlayerReady(true);
          event.target.playVideo();
        },
        onStateChange: (event) => {
          // Loop video when it ends
          if (event.data === window.YT.PlayerState.ENDED) {
            event.target.seekTo(startTime);
            event.target.playVideo();
          }
        }
      }
    });
  }, [videoId, startTime]);

  // Handle scroll for parallax
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle mute toggle
  const toggleMute = useCallback(() => {
    if (playerRef.current && playerReady) {
      if (isMuted) {
        playerRef.current.unMute();
        playerRef.current.setVolume(50);
      } else {
        playerRef.current.mute();
      }
      setIsMuted(!isMuted);
    }
  }, [isMuted, playerReady]);

  const parallaxOffset = scrollY * parallaxSpeed;

  return (
    <div 
      ref={containerRef}
      className={`absolute inset-0 overflow-hidden ${className}`}
      style={{ zIndex: 0 }}
    >
      {/* Video Container with Parallax */}
      <div 
        className="absolute inset-0"
        style={{
          transform: `translateY(${parallaxOffset}px) scale(1.3)`,
          top: '-15%',
          height: '130%'
        }}
      >
        <div
          id={playerIdRef.current}
          className="absolute w-full h-full pointer-events-none"
          style={{
            border: 'none',
            width: '100vw',
            height: '100vh',
            minWidth: '177.77vh',
            minHeight: '56.25vw',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)'
          }}
        />
      </div>

      {/* Dark Gradient Overlay */}
      {overlay && (
        <>
          <div 
            className="absolute inset-0"
            style={{ 
              background: `linear-gradient(to bottom, 
                rgba(11, 15, 20, 0.4) 0%, 
                rgba(11, 15, 20, 0.6) 50%, 
                rgba(11, 15, 20, 0.95) 100%)`,
              opacity: overlayOpacity 
            }}
          />
          <div 
            className="absolute inset-0"
            style={{ 
              background: 'radial-gradient(ellipse at center, transparent 0%, rgba(11, 15, 20, 0.8) 100%)'
            }}
          />
        </>
      )}

      {/* Mute/Unmute Button */}
      {showMuteButton && (
        <button
          onClick={toggleMute}
          className="fixed bottom-6 right-6 z-[100] p-3 rounded-full bg-black/60 backdrop-blur-md border border-white/20 hover:bg-white/20 hover:scale-110 transition-all duration-300 group shadow-lg"
          title={isMuted ? 'Ton einschalten' : 'Ton ausschalten'}
          data-testid="video-mute-btn"
        >
          {isMuted ? (
            <VolumeX className="w-5 h-5 text-white/70 group-hover:text-white" />
          ) : (
            <Volume2 className="w-5 h-5 text-cyan-400 group-hover:text-cyan-300 animate-pulse" />
          )}
        </button>
      )}
    </div>
  );
};

/**
 * Parallax Video Section - Shows video clearly with parallax effect
 */
export const ParallaxVideoSection = ({
  videoId = "frLPZkVTQUg",
  startTime = 14,
  height = "60vh",
  children,
  overlayContent,
  className = ""
}) => {
  const [scrollY, setScrollY] = useState(0);
  const sectionRef = useRef(null);
  const [sectionTop, setSectionTop] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
      
      if (sectionRef.current) {
        const rect = sectionRef.current.getBoundingClientRect();
        setSectionTop(rect.top + window.scrollY);
        setIsVisible(rect.top < window.innerHeight && rect.bottom > 0);
      }
    };
    
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const parallaxOffset = (scrollY - sectionTop) * 0.4;

  const embedUrl = `https://www.youtube.com/embed/${videoId}?` + [
    'autoplay=1',
    'mute=1',
    'loop=1',
    `playlist=${videoId}`,
    `start=${startTime}`,
    'controls=0',
    'showinfo=0',
    'rel=0',
    'modestbranding=1',
    'playsinline=1',
    'iv_load_policy=3',
    'disablekb=1',
    'origin=' + window.location.origin
  ].join('&');

  return (
    <section 
      ref={sectionRef}
      className={`relative overflow-hidden ${className}`}
      style={{ height }}
    >
      {/* Parallax Video */}
      <div 
        className="absolute inset-0"
        style={{
          transform: `translateY(${parallaxOffset}px)`,
          top: '-20%',
          height: '140%'
        }}
      >
        {isVisible && (
          <iframe
            src={embedUrl}
            className="absolute w-full h-full pointer-events-none"
            style={{
              border: 'none',
              minWidth: '177.77vh',
              minHeight: '56.25vw',
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)'
            }}
            allow="autoplay; encrypted-media"
            allowFullScreen
            title="Parallax Video"
            loading="lazy"
          />
        )}
      </div>

      {/* Gradient Overlays for smooth blending */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0B0F14] via-transparent to-[#0B0F14] opacity-90" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0B0F14]/60 via-transparent to-[#0B0F14]/60" />
      
      {/* Vignette effect */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse at center, transparent 30%, rgba(11, 15, 20, 0.8) 100%)'
        }}
      />

      {/* Content Overlay */}
      {overlayContent && (
        <div className="absolute inset-0 flex items-center justify-center z-10">
          {overlayContent}
        </div>
      )}

      {children}
    </section>
  );
};

export default VideoBackground;
